import { useState, useEffect, useRef, useContext } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import { Grid, Button, Typography, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailTemplate from "./components/EmailTemplate";
import TemplateSelect from "./components/TemplateSelect";
import RecipientFilters from "./components/RecipientFilters";
import NewTemplateDialog from "./components/NewTemplateDialog";
import BulkSendDialog from "./components/BulkSendDialog";
import MsgStepper from "./components/MsgStepper";
import { EventContext } from "../../contexts/EventContext";
import { MessengerContext } from "../../contexts/MessengerContext";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const Messenger = ({ pairingEvent, customSender }) => {
  const location = useLocation();
  const emailTemplateRef = useRef();
  const { userData } = useContext(EventContext);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [canSend, setCanSend] = useState(false);
  const [saveAs, setSaveAs] = useState(false);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [sendAt, setSendAt] = useState(null);
  const [channel, setChannel] = useState('email');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/messenger",
      title: "Messenger",
    });
  }, []);

  useEffect(() => {
    setTemplateId(location?.state?.templateId);
  }, [location]);

  useEffect(() => {
    getTemplate();
  }, [templateId]);

  useEffect(() => {
    if (templates.length === 0) {
      getTemplates();
    }
  }, [userData]);

  useEffect(() => {
    if (templateId && recipientFilter) {
      if (recipientFilter === "custom" && recipients.length > 0) {
        setCanSend(true);
      } else if (recipientFilter !== "custom") {
        setCanSend(true);
      } else {
        setCanSend(false);
      }
    } else {
      setCanSend(false);
    }
  }, [templateId, recipientFilter, recipients]);

  const getTemplates = async () => {
    if (!userData) return;
    const result = await request(`templates/?orgId=${userData?.org?._id}`);
    if (result.success) {
      setTemplates(result.templates);
    }
  };

  const getTemplate = async () => {
    if (templateId && templateId !== "custom") {
      const result = await request(`templates/?templateId=${templateId}`);
      if (result.success) {
        setTemplate(result.template);
        console.log("template retrieved");
      }
    }
  };

  const handleSave = async (data, saveAsNew = false) => {
    try {
      if (saveAsNew) setSaveAs(true);
      if (!data._id && !data.name) {
        setSaveOpen(true);
        return;
      } else if (!data._id || saveAs) {
        if (!data.subject) {
          toast.error("Please enter a subject");
          return;
        }
        if (data._id) {
          delete data._id;
        }

        const result = await request("templates/create", "post", data);
        if (result.success) {
          getTemplates();
          setTemplateId(result.templateId);
          toast.success("Template created successfully!");
          setSaveOpen(false);
          setSaveAs(false);
        }
      } else {
        const body = {
          templateId: data._id,
          update: {
            htmlBody: data.htmlBody,
            jsonBody: data.jsonBody,
            subject: data.subject,
          },
        };
        const result = await request("templates/edit", "post", body);
        if (result.success) {
          toast.success("Template updated successfully!");
          // handleClose();
        }
      }
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  const handleSend = async (data) => {
    setSending(true);
    try {
      data.pairingEventId = pairingEvent._id;
      data.recipientFilter = recipientFilter;
      data.recipients = recipients;
      data.channel = channel;
      data.sendAt = sendAt;
      data.template = template?.name;
      const result = await request("templates/send", "post", data);
      if (result.success) {
        if (result.message) {
          toast.success(result.message);
        } else {
          toast.success("Message sent successfully");
        }
      }
    } catch (error) {
      console.error("Error sending template:", error);
    }
    setSending(false);
  };

  const renderDelete = () => {
    if (template && template?.baseTemplate === false && !confirmDelete) {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setConfirmDelete(true)}
        >
          delete
        </Button>
      );
    } else if (template && template?.baseTemplate === false && confirmDelete) {
      return (
        <Button variant="contained" size="small" onClick={handleDelete}>
          confirm delete
        </Button>
      );
    }
  };

  const handleDelete = async () => {
    const result = await request("templates/delete", "post", { templateId });
    if (result.success) {
      getTemplates();
      resetTemplate();
      setConfirmDelete(false);
      toast.success("Template deleted successfully");
    }
  };

  const saveNewTemplate = (name) => {
    emailTemplateRef.current?.handleSave(name);
  };

  const resetTemplate = () => {
    emailTemplateRef.current?.handleReset();
    setTemplate(null);
    setTemplateId(null);
  };

  return (
    <MessengerContext.Provider
      value={{
        templates,
        setTemplates,
        templateId,
        setTemplateId,
        template,
        setTemplate,
        recipientFilter,
        setRecipientFilter,
        recipients,
        setRecipients,
        canSend,
        sendAt,
        setSendAt,
        channel,
        setChannel,
        sending
      }}
    >
      <Grid
        container
        spacing={3}
        style={{ height: "100%" }}
        justifyContent="center"
      >
        {pairingEvent ? null : (
          <Grid
            item
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              gap: 10,
            }}
            xs={12}
          >
            <TemplateSelect />
            <Tooltip
              title="To start either select an existing template from the drop down or
          start designing a new one. Be sure to click save when done."
            >
              <HelpOutlineIcon style={{ color: "grey" }} />
            </Tooltip>
            <Button onClick={resetTemplate}>Reset</Button>

            {renderDelete()}
            {templateId && userData?.role === "superadmin" ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setBulkSendOpen(true)}
              >
                Bulk send
              </Button>
            ) : null}
          </Grid>
        )}

        {pairingEvent ? <MsgStepper /> : null}
        <Grid item xs={12} style={{ height: "100%" }}>
          <EmailTemplate
            template={template}
            update={handleSave}
            send={handleSend}
            ref={emailTemplateRef}
            pairingEvent={pairingEvent}
            customSender={customSender}
          />
        </Grid>

        <NewTemplateDialog
          open={saveOpen}
          close={() => {
            setSaveOpen(false);
          }}
          save={(name) => saveNewTemplate(name)}
        />

        {userData?.role === "superadmin" ? (
          <BulkSendDialog
            open={bulkSendOpen}
            close={() => setBulkSendOpen(false)}
            templateId={templateId}
          />
        ) : null}
      </Grid>
    </MessengerContext.Provider>
  );
};

export default Messenger;
